import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Empty, Popover, Row, Segmented, Space, Tag, Tooltip, Typography } from 'antd';
import DataTable from '@/components/DataTable';
import Axios from 'axios';
import { toast } from 'react-toastify';

const { Text } = Typography;
const bp = '/bp';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });

const ROLE_META = {
  API: { label: 'Active ingredient', color: '#0e6c7a', order: 1 },
  EXCIPIENT: { label: 'Excipients', color: '#64748b', order: 2 },
  PH_ADJUST: { label: 'pH adjusters', color: '#7c3aed', order: 3 },
  SOLVENT: { label: 'Solvents / vehicle', color: '#0891b2', order: 4 },
  GAS: { label: 'Process gases', color: '#65a30d', order: 5 },
  PROCESS_AID: { label: 'Processing aids', color: '#ea580c', order: 6 },
  PRIMARY_PM: { label: 'Primary packaging', color: '#ca8a04', order: 7 },
  SECONDARY_PM: { label: 'Secondary packaging', color: '#a16207', order: 8 },
  OTHER: { label: 'Other (authored on the recipe)', color: '#94a3b8', order: 9 },
};
const fmt = (n, d = 6) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const nice = (q, u) => {
  // 4 000 000 mg reads badly — show the human unit alongside
  if (q == null) return '—';
  const v = Number(q); const unit = (u || '').toLowerCase();
  if (unit === 'mg' && v >= 1e6) return `${fmt(v / 1e6, 3)} kg`;
  if (unit === 'mg' && v >= 1e3) return `${fmt(v / 1e3, 3)} g`;
  if (unit === 'g' && v >= 1e3) return `${fmt(v / 1e3, 3)} kg`;
  if (unit === 'ml' && v >= 1e3) return `${fmt(v / 1e3, 3)} L`;
  return `${fmt(v)} ${u || ''}`.trim();
};

/**
 * MBOM — the MFR evaluated at the MBMR's batch size. Grouped by material role, one summary strip,
 * human units first (exact MFR-unit value on hover), the operation each line is charged at, and
 * the formula/inputs behind every number. `operationId` narrows it to one step (operator view).
 */
export default function MbomPanel({ recipeId, operationId, compact = false, title = 'Master Bill of Materials' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('by-role');

  const load = useCallback(async () => {
    if (!recipeId) return;
    setLoading(true);
    try {
      const res = await Axios.get(`${bp}/recipe/${recipeId}/mbom/`, { params: operationId ? { operation_id: operationId } : {}, ...auth() });
      setData(res?.data?.data || null);
    } catch (e) { setData(null); }
    setLoading(false);
  }, [recipeId, operationId]);
  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    try { const r = await Axios.post(`${bp}/recipe/${recipeId}/mbom/rebuild/`, {}, auth()); toast.success(r?.data?.message || 'MBOM rebuilt'); setData(r?.data?.data || null); }
    catch (e) { toast.warning(e?.response?.data?.message || 'Rebuild failed'); }
  };
  const exportCsv = async () => {
    try {
      const r = await Axios.get(`${bp}/recipe/${recipeId}/mbom/`, { params: { output: 'csv' }, responseType: 'blob', ...auth() });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `MBOM_recipe_${recipeId}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.warning('Export failed'); }
  };

  const groups = useMemo(() => {
    const lines = data?.lines || [];
    if (view === 'by-stage') {
      const m = {};
      lines.forEach((l) => { const k = l.charged_at?.[0] ? `${l.charged_at[0].number != null ? `${l.charged_at[0].number}. ` : ''}${l.charged_at[0].name}` : (l.is_qs ? 'Endpoint (q.s.)' : 'Not placed'); (m[k] = m[k] || []).push(l); });
      return Object.entries(m).map(([k, v]) => ({ key: k, label: k, color: k === 'Not placed' ? '#dc2626' : '#0e6c7a', lines: v }));
    }
    const m = {};
    lines.forEach((l) => { (m[l.role || 'OTHER'] = m[l.role || 'OTHER'] || []).push(l); });
    return Object.entries(m).sort((a, b) => (ROLE_META[a[0]]?.order || 99) - (ROLE_META[b[0]]?.order || 99))
      .map(([k, v]) => ({ key: k, label: ROLE_META[k]?.label || k, color: ROLE_META[k]?.color || '#94a3b8', lines: v }));
  }, [data, view]);

  if (!recipeId) return null;

  const formula = (l) => (
    <div style={{ maxWidth: 420, fontSize: 12 }}>
      <div><b>Formula</b> <code>{l.expression || '—'}</code></div>
      {l.inputs && Object.keys(l.inputs).length > 0 && <div style={{ marginTop: 4 }}><b>Inputs</b> {Object.entries(l.inputs).map(([k, v]) => `${k} = ${v}`).join(' · ')}</div>}
      {l.per_basis != null && <div style={{ marginTop: 4 }}><b>Per unit</b> {fmt(l.per_basis)} {l.per_basis_uom}</div>}
      {l.mfr_line_id && data?.mfr && <div style={{ marginTop: 4, color: '#64748b' }}>{data.mfr.mfr_no} v{data.mfr.version} · line {l.sequence}</div>}
      {l.override_reason && <div style={{ marginTop: 4, color: '#b45309' }}>Overridden: {l.override_reason}</div>}
    </div>
  );

  const columns = [
    { title: 'Material', dataIndex: 'material', render: (v, l) => (
      <div><div style={{ fontWeight: 600 }}>{v}</div><Text type="secondary" style={{ fontSize: 12 }}>{l.sap_code || '—'}{l.potency_basis && l.potency_basis !== 'NONE' ? ` · potency ${l.potency_basis.replace('_', ' ').toLowerCase()}` : ''}{l.overage_percent ? ` · +${l.overage_percent} % overage` : ''}</Text></div>) },
    { title: 'Quantity for this batch', width: 210, align: 'right', render: (_, l) => (l.is_qs
      ? <Tag color="cyan" style={{ marginRight: 0 }}>q.s. {l.qs_target}</Tag>
      : <Popover content={formula(l)} title="How this number was derived"><div style={{ cursor: 'help' }}><div style={{ fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{nice(l.quantity, l.uom)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{fmt(l.quantity)} {l.uom}{l.tolerance_percent != null ? ` · ±${l.tolerance_percent} %` : ''}</Text></div></Popover>) },
    { title: 'Dispense as', width: 130, align: 'right', render: (_, l) => (l.dispense_quantity != null ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(l.dispense_quantity, 4)} {l.dispense_uom}</span> : '—') },
    { title: 'Charged at', render: (_, l) => (l.charged_at?.length
      ? l.charged_at.map((p) => <Tag key={p.ingredient_row_id} style={{ marginBottom: 2 }}>{p.number != null ? `${p.number}. ` : ''}{p.name}</Tag>)
      : (l.is_qs ? <Text type="secondary">endpoint of the step</Text> : <Tag color="error">not placed</Tag>)) },
  ];

  const summary = data && (
    <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
      {[
        ['Batch', data.batch_size != null ? `${fmt(data.batch_size)} ${data.batch_uom}` : '—'],
        ['Theoretical output', data.theoretical_output != null ? `${fmt(data.theoretical_output, 0)} ${data.output_uom}` : '—'],
        ['Bulk mass', data.bulk_mass_kg != null ? `${fmt(data.bulk_mass_kg, 2)} kg` : '—'],
        ['Lines', `${(data.lines || []).length}${data.unplaced_lines?.length ? ` · ${data.unplaced_lines.length} not placed` : ''}`],
        ['Source', data.mfr ? `${data.mfr.mfr_no} v${data.mfr.version}` : 'authored'],
      ].map(([k, v]) => (
        <Col key={k} flex="1 1 0"><div style={{ border: '1px solid rgba(100,116,139,.25)', borderRadius: 8, padding: '6px 10px' }}>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>{k}</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{v}</div></div></Col>
      ))}
    </Row>
  );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 8 }}>
        <Space wrap>
          <Text strong style={{ fontSize: 15 }}>{title}</Text>
          {data && <Tag>v{data.version}</Tag>}
          {data?.source && <Tag color={data.source === 'DERIVED' ? 'geekblue' : 'default'}>{data.source === 'DERIVED' ? 'derived from MFR' : 'authored'}</Tag>}
          {data?.mfr && <Tag color={data.mfr.stale ? 'error' : 'success'}>{data.mfr.stale ? 'MFR superseded — re-derive' : 'MFR current'}</Tag>}
        </Space>
        <Space wrap>
          {!operationId && <Segmented size="small" value={view} onChange={setView} options={[{ value: 'by-role', label: 'By material role' }, { value: 'by-stage', label: 'By charging step' }]} />}
          <Button size="small" onClick={rebuild}>Rebuild</Button>
          <Button size="small" onClick={exportCsv}>Export CSV</Button>
        </Space>
      </div>
      {loading && <Text type="secondary">Loading…</Text>}
      {!loading && !data && <Empty description="No MBOM — this MBMR was not derived from an MFR" />}
      {data && (
        <>
          {!compact && summary}
          {data.mfr?.stale && <Alert type="error" showIcon style={{ marginBottom: 8 }} message="The MFR this MBOM came from is no longer the current version. Re-derive the MBMR before issuing a batch." />}
          {!operationId && data.unplaced_lines?.length > 0 && <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={`${data.unplaced_lines.length} line(s) are not charged on any operation — place them before submitting the MBMR for approval.`} />}
          {groups.map((g) => (
            <div key={g.key} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
                <span style={{ width: 4, height: 18, borderRadius: 2, background: g.color, display: 'inline-block' }} />
                <Text strong>{g.label}</Text><Text type="secondary">· {g.lines.length}</Text>
              </div>
              <DataTable height={null} size="small" rowKey="sequence" pagination={false} showHeader={g === groups[0]} columns={columns} data={g.lines} />
            </div>
          ))}
          <Text type="secondary" style={{ fontSize: 12 }}>Hover a quantity for the formula and inputs behind it. Quantities come from the MFR; change the MFR (new version) or override with a reason — never retype here.</Text>
        </>
      )}
    </div>
  );
}
